/**
 * PDF Generator Module
 * Professional PDF generation for SkillSprint Challenge profile reports
 * Features: Modern design, clear typography, structured sections
 */
import { jsPDF } from 'jspdf';
import { ResultData } from '../types/results';

interface PdfGeneratorOptions {
  results: ResultData;
  profileText: string;
}

// =============================================================================
// DESIGN CONSTANTS - Minimalist Color Palette
// =============================================================================

const COLORS = {
  // Primary palette (Scaleo blue)
  primary: [66, 165, 245] as [number, number, number],       // #42a5f5
  primaryLight: [227, 242, 253] as [number, number, number], // Light blue bg
  primaryDark: [30, 136, 229] as [number, number, number],   // Darker blue
  
  // Recommendations green (same shade level as primary blue)
  recsGreen: [66, 186, 150] as [number, number, number],     // #42BA96 - matches blue intensity
  recsGreenDark: [46, 166, 130] as [number, number, number], // Darker green
  recsGreenLight: [232, 248, 243] as [number, number, number], // Light green bg
  
  // Status colors
  success: [76, 175, 80] as [number, number, number],        // #4caf50
  successLight: [232, 245, 233] as [number, number, number], // Light green bg
  warning: [251, 192, 45] as [number, number, number],       // #fbc02d
  warningLight: [255, 248, 225] as [number, number, number], // Light yellow bg
  danger: [244, 67, 54] as [number, number, number],         // #f44336
  
  // Neutrals - minimalist grays
  text: [33, 33, 33] as [number, number, number],            // Near black
  textMuted: [117, 117, 117] as [number, number, number],    // Gray-600
  textLight: [158, 158, 158] as [number, number, number],    // Gray-400
  white: [255, 255, 255] as [number, number, number],
  background: [245, 245, 245] as [number, number, number],   // #f5f5f5
  backgroundDark: [207, 207, 207] as [number, number, number], // #cfcfcf
  border: [224, 224, 224] as [number, number, number],       // #e0e0e0
};

const FONTS = {
  titleLarge: 24,
  titleMedium: 18,
  titleSmall: 14,
  heading1: 13,
  heading2: 12,
  heading3: 11,
  body: 10,
  bodySmall: 9,
  caption: 8,
};

const SPACING = {
  margin: 20,
  sectionGap: 12,
  paragraphGap: 6,
  lineHeight: 1.4,
};

// =============================================================================
// MAIN PDF GENERATOR
// =============================================================================

/**
 * Generate professional PDF from profile analysis
 */
export const generateProfilePdf = ({ results, profileText }: PdfGeneratorOptions): jsPDF | null => {
  if (!profileText || !results) return null;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = SPACING.margin;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition = margin;
  let currentSection = '';

  // ==========================================================================
  // HELPER FUNCTIONS
  // ==========================================================================

  const checkNewPage = (neededHeight: number = 15): boolean => {
    if (yPosition + neededHeight > pageHeight - margin - 15) {
      pdf.addPage();
      yPosition = margin + 5;
      // Add subtle header on continuation pages
      addContinuationHeader();
      return true;
    }
    return false;
  };

  const addContinuationHeader = () => {
    pdf.setFillColor(...COLORS.primaryLight);
    pdf.rect(0, 0, pageWidth, 12, 'F');
    pdf.setFontSize(FONTS.caption);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.primary);
    pdf.text('SKILLSPRINT CHALLENGE - Rapport de Profil (suite)', margin, 8);
    yPosition = 20;
  };

  const addWrappedText = (
    text: string, 
    fontSize: number, 
    isBold: boolean = false, 
    color: [number, number, number] = COLORS.text,
    indent: number = 0
  ): number => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    pdf.setTextColor(...color);
    
    const effectiveWidth = maxWidth - indent;
    const lines = pdf.splitTextToSize(text, effectiveWidth);
    const lineHeight = fontSize * 0.45;
    
    lines.forEach((line: string) => {
      checkNewPage(lineHeight);
      pdf.text(line, margin + indent, yPosition);
      yPosition += lineHeight;
    });
    
    return lines.length * lineHeight;
  };

  const addSectionDivider = () => {
    yPosition += 4;
    checkNewPage(10);
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
  };

  // Helper: Strip markdown asterisks from text (e.g., *text* or **text**)
  const stripAsterisks = (text: string): string => {
    return text.replace(/\*+([^*]+)\*+/g, '$1').replace(/\*/g, '');
  };

  // Helper: Check if line should be skipped (e.g., "Rapport genere par SkillSprint Challenge...")
  const shouldSkipLine = (line: string): boolean => {
    const skipPatterns = [
      /rapport\s+genere\s+par\s+skillsprint/i,
      /votre\s+partenaire\s+developpeme/i
    ];
    return skipPatterns.some(pattern => pattern.test(line));
  };

  // ==========================================================================
  // COVER HEADER - Minimalist Design
  // ==========================================================================

  // Clean header with primary color
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, pageWidth, 40, 'F');

  // Main title
  pdf.setFontSize(FONTS.titleLarge);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.white);
  pdf.text('SKILLSPRINT CHALLENGE', margin, 18);

  // Subtitle
  pdf.setFontSize(FONTS.titleSmall);
  pdf.setFont('helvetica', 'normal');
  pdf.text("Rapport de Profil d'Apprentissage", margin, 30);

  yPosition = 50;

  // ==========================================================================
  // CANDIDATE INFO CARD - Clean minimalist style
  // ==========================================================================

  const cardHeight = 35;
  pdf.setFillColor(...COLORS.background);
  pdf.roundedRect(margin, yPosition, maxWidth, cardHeight, 2, 2, 'F');
  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(margin, yPosition, maxWidth, cardHeight, 2, 2, 'S');

  // Card title
  pdf.setFontSize(FONTS.heading2);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Résultats du Test', margin + 6, yPosition + 7);

  // Info grid - Row 1
  const col1 = margin + 6;
  const col2 = margin + 55;
  const col3 = margin + 110;
  
  pdf.setFontSize(FONTS.bodySmall);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.textMuted);
  
  pdf.text('Score global', col1, yPosition + 15);
  pdf.text('Réponses correctes', col2, yPosition + 15);
  pdf.text('Date du test', col3, yPosition + 15);

  pdf.setFontSize(FONTS.body);
  pdf.setFont('helvetica', 'bold');
  
  // Score with color coding
  const scoreColor = results.percentage >= 70 ? COLORS.success : 
                     results.percentage >= 50 ? COLORS.warning : 
                     COLORS.danger;
  pdf.setTextColor(...scoreColor);
  pdf.text(`${results.percentage}%`, col1, yPosition + 21);
  
  pdf.setTextColor(...COLORS.text);
  pdf.text(`${results.correct_answers}/${results.total_questions}`, col2, yPosition + 21);
  pdf.text(results.completed_at ? new Date(results.completed_at).toLocaleDateString('fr-FR') : '-', col3, yPosition + 21);

  // Info grid - Row 2 labels
  pdf.setFontSize(FONTS.bodySmall);
  pdf.setTextColor(...COLORS.textMuted);
  pdf.setFont('helvetica', 'normal');
  
  pdf.text('Durée totale', col1, yPosition + 27);
  pdf.text('Points obtenus', col2, yPosition + 27);

  // Info grid - Row 2 values
  pdf.setFontSize(FONTS.body);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...COLORS.text);
  
  pdf.text(results.total_time_formatted || '-', col1, yPosition + 33);
  pdf.text(`${results.total_score}/${results.max_score}`, col2, yPosition + 33);

  yPosition += cardHeight + 15;

  // ==========================================================================
  // PARSE AND RENDER PROFILE ANALYSIS
  // ==========================================================================

  const lines = profileText.split('\n');
  let inList = false;
  let tableHeaders: string[] = [];
  let isFirstTableRow = true;
  
  lines.forEach((line, lineIndex) => {
    let trimmedLine = line.trim();
    
    // Skip lines that should be excluded
    if (shouldSkipLine(trimmedLine)) {
      return;
    }
    
    // Strip asterisks from the line (fix markdown formatting issues)
    trimmedLine = stripAsterisks(trimmedLine);
    
    // Empty lines
    if (!trimmedLine) {
      if (inList) {
        yPosition += 3;
        inList = false;
      }
      yPosition += 2;
      return;
    }

    // Section separators (---)
    if (trimmedLine.startsWith('---')) {
      addSectionDivider();
      return;
    }

    // ========== MAIN SECTION HEADERS ==========
    // Detect: ## HEADER, # HEADER, or ALL CAPS HEADERS (like "TABLEAU DES SCORES")
    const isMainHeader = trimmedLine.startsWith('## ') || 
                         (trimmedLine.startsWith('# ') && !trimmedLine.startsWith('###')) ||
                         (trimmedLine === trimmedLine.toUpperCase() && 
                          trimmedLine.length > 5 && 
                          trimmedLine.length < 60 &&
                          !trimmedLine.includes('|') &&
                          !trimmedLine.startsWith('-') &&
                          !trimmedLine.startsWith('**') &&
                          /^[A-Z\s\d':]+$/.test(trimmedLine));
    
    if (isMainHeader) {
      checkNewPage(25);
      yPosition += 6;
      
      const headerText = trimmedLine.replace(/^#+\s*/, '').toUpperCase();
      currentSection = headerText;
      
      // Clean accent bar on left only (no background)
      pdf.setFillColor(...COLORS.primary);
      pdf.rect(margin, yPosition - 3, 3, 10, 'F');
      
      // Section title - 14pt bold
      pdf.setFontSize(FONTS.titleSmall);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.primary);
      pdf.text(headerText, margin + 8, yPosition + 4);
      
      yPosition += 14;
      return;
    }

    // ========== SUB-SECTION HEADERS (###) ==========
    if (trimmedLine.startsWith('### ')) {
      checkNewPage(16);
      yPosition += 5;
      
      const headerText = trimmedLine.replace('### ', '');
      
      // Clean design - just underline accent
      pdf.setFontSize(FONTS.heading2);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.text);
      pdf.text(headerText, margin, yPosition + 3);
      
      // Subtle underline
      pdf.setDrawColor(...COLORS.primary);
      pdf.setLineWidth(0.5);
      const textWidth = pdf.getTextWidth(headerText);
      pdf.line(margin, yPosition + 5, margin + textWidth, yPosition + 5);
      
      yPosition += 12;
      return;
    }

    // ========== SUB-SUB HEADERS (####) ==========
    if (trimmedLine.startsWith('#### ')) {
      checkNewPage(12);
      yPosition += 4;
      
      const headerText = trimmedLine.replace('#### ', '');
      
      pdf.setFontSize(FONTS.heading3);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.text);
      pdf.text('► ' + headerText, margin, yPosition);
      
      yPosition += 8;
      return;
    }

    // ========== BOLD TEXT (**text**) ==========
    // Detect lines wrapped in **
    if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
      checkNewPage(10);
      const boldText = trimmedLine.replace(/\*\*/g, '');
      
      // Clean bold text - no background
      pdf.setFontSize(FONTS.body);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.text);
      pdf.text(boldText, margin, yPosition);
      
      yPosition += 6;
      return;
    }
    
    // ========== INLINE BOLD TEXT (**text**: more text) ==========
    if (trimmedLine.includes('**') && !trimmedLine.startsWith('-')) {
      checkNewPage(10);
      
      // Parse bold parts
      const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/);
      let xPos = margin;
      
      pdf.setFontSize(FONTS.body);
      
      parts.forEach((part) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // Bold part
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...COLORS.text);
          const text = part.replace(/\*\*/g, '');
          pdf.text(text, xPos, yPosition);
          xPos += pdf.getTextWidth(text);
        } else if (part) {
          // Normal part
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...COLORS.text);
          pdf.text(part, xPos, yPosition);
          xPos += pdf.getTextWidth(part);
        }
      });
      
      yPosition += 6;
      return;
    }

    // ========== LABEL LINES (Label: text) ==========
    // Detect lines that start with a label pattern like "Dimension:", "Le problème:", "Solutions:"
    const labelMatch = trimmedLine.match(/^([A-Za-zÀ-ÿ\s]+)\s*:\s*(.*)$/);
    if (labelMatch && !trimmedLine.includes('|') && trimmedLine.indexOf(':') < 25) {
      checkNewPage(10);
      const label = labelMatch[1].trim();
      const value = labelMatch[2].trim();
      
      pdf.setFontSize(FONTS.body);
      
      // Bold label with accent color
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.primary);
      pdf.text(label + ' : ', margin, yPosition);
      const labelWidth = pdf.getTextWidth(label + ' : ');
      
      // Normal value
      if (value) {
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...COLORS.text);
        pdf.text(value, margin + labelWidth, yPosition);
      }
      
      yPosition += 6;
      return;
    }

    // ========== BULLET LIST ITEMS ==========
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
      checkNewPage(10);
      inList = true;
      
      const bulletText = trimmedLine.replace(/^[-•]\s*/, '');
      
      // Custom bullet point
      pdf.setFillColor(...COLORS.primary);
      pdf.circle(margin + 3, yPosition - 1, 1.2, 'F');
      
      pdf.setFontSize(FONTS.body);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...COLORS.text);
      
      const wrappedLines = pdf.splitTextToSize(bulletText, maxWidth - 12);
      wrappedLines.forEach((wLine: string, idx: number) => {
        checkNewPage(5);
        pdf.text(wLine, margin + 8, yPosition);
        yPosition += 5;
      });
      
      yPosition += 1;
      return;
    }

    // ========== NUMBERED LIST ITEMS ==========
    if (/^\d+\.\s/.test(trimmedLine)) {
      checkNewPage(12);
      inList = true;
      
      const match = trimmedLine.match(/^(\d+)\.\s*(.*)/);
      if (match) {
        const number = match[1];
        const text = match[2];
        
        // Number badge - rounded rectangle for better number display
        pdf.setFillColor(...COLORS.primary);
        pdf.roundedRect(margin, yPosition - 4, 8, 8, 2, 2, 'F');
        
        // Number text - centered in badge
        pdf.setFontSize(FONTS.body);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...COLORS.white);
        const numWidth = pdf.getTextWidth(number);
        pdf.text(number, margin + 4 - numWidth/2, yPosition + 1);
        
        // List item text
        pdf.setFontSize(FONTS.body);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...COLORS.text);
        
        const wrappedLines = pdf.splitTextToSize(text, maxWidth - 14);
        wrappedLines.forEach((wLine: string, idx: number) => {
          checkNewPage(6);
          pdf.text(wLine, margin + 12, yPosition);
          yPosition += 6;
        });
        
        yPosition += 3;
      }
      return;
    }

    // ========== TABLE HANDLING ==========
    if (trimmedLine.includes('|') && trimmedLine.startsWith('|')) {
      // Skip separator rows (|---|---|)
      if (trimmedLine.match(/^\|[\s\-:]+\|/)) {
        // Check if it's primarily dashes
        const dashCount = (trimmedLine.match(/-/g) || []).length;
        if (dashCount > 3) {
          // This is a separator row, skip it
          return;
        }
      }
      
      const cells = trimmedLine.split('|').filter(c => c.trim()).map(c => c.trim());
      
      if (cells.length >= 2) {
        checkNewPage(12);
        
        const colWidth = maxWidth / cells.length;
        const rowHeight = 10;
        
        // Header row styling
        if (isFirstTableRow) {
          tableHeaders = cells;
          
          // Header background
          pdf.setFillColor(...COLORS.primary);
          pdf.rect(margin, yPosition - 5, maxWidth, rowHeight, 'F');
          
          // Header border
          pdf.setDrawColor(...COLORS.primaryDark);
          pdf.setLineWidth(0.5);
          pdf.rect(margin, yPosition - 5, maxWidth, rowHeight, 'S');
          
          // Cell borders for header
          cells.forEach((cell, idx) => {
            if (idx > 0) {
              pdf.line(margin + (idx * colWidth), yPosition - 5, margin + (idx * colWidth), yPosition - 5 + rowHeight);
            }
          });
          
          pdf.setFontSize(FONTS.body);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(...COLORS.white);
          
          cells.forEach((cell, idx) => {
            pdf.text(cell.substring(0, 35), margin + 4 + (idx * colWidth), yPosition + 1);
          });
          
          isFirstTableRow = false;
        } else {
          // Data row background
          pdf.setFillColor(...COLORS.background);
          pdf.rect(margin, yPosition - 5, maxWidth, rowHeight, 'F');
          
          // Data row border
          pdf.setDrawColor(...COLORS.border);
          pdf.setLineWidth(0.3);
          pdf.rect(margin, yPosition - 5, maxWidth, rowHeight, 'S');
          
          // Cell borders for data rows
          cells.forEach((cell, idx) => {
            if (idx > 0) {
              pdf.line(margin + (idx * colWidth), yPosition - 5, margin + (idx * colWidth), yPosition - 5 + rowHeight);
            }
          });
          
          pdf.setFontSize(FONTS.body);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...COLORS.text);
          
          cells.forEach((cell, idx) => {
            pdf.text(cell.substring(0, 35), margin + 4 + (idx * colWidth), yPosition + 1);
          });
        }
        
        yPosition += rowHeight;
      }
      return;
    }

    // Reset table state on non-table content
    isFirstTableRow = true;

    // ========== REGULAR PARAGRAPHS ==========
    addWrappedText(trimmedLine, FONTS.body, false, COLORS.text);
    yPosition += 3;
  });

  // ==========================================================================
  // FOOTER ON ALL PAGES
  // ==========================================================================

  const totalPages = pdf.internal.pages.length - 1;
  
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    
    // Footer line
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.3);
    pdf.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
    
    // Footer text
    pdf.setFontSize(FONTS.caption);
    pdf.setTextColor(...COLORS.textLight);
    pdf.setFont('helvetica', 'normal');
    
    // Left: branding
    pdf.text('SkillSprint Challenge', margin, pageHeight - 12);
    
    // Center: date
    const dateText = `Genere le ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    pdf.text(dateText, pageWidth / 2, pageHeight - 12, { align: 'center' });
    
    // Right: page number
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
  }

  return pdf;
};

/**
 * Generate PDF blob URL for preview
 */
export const generatePdfBlobUrl = (pdf: jsPDF): string => {
  const blob = pdf.output('blob');
  return URL.createObjectURL(blob);
};

/**
 * Download PDF with generated filename
 */
export const downloadProfilePdf = (pdf: jsPDF, version: string): void => {
  const fileName = `Profil_SkillSprint_Challenge_${version}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};

// =============================================================================
// RECOMMENDATIONS PDF GENERATOR
// =============================================================================

// Helper: Get username from localStorage
const getUsername = (): string => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return localStorage.getItem('username') || 'Utilisateur';
  }
  return 'Utilisateur';
};

interface RecommendationsPdfOptions {
  profileName: string;
  recommendations: {
    exploiter_forces?: {
      introduction?: string;
      strategies?: Array<{
        titre: string;
        description: string;
        action_immediate: string;
      }>;
      pieges_a_eviter?: string[];
    };
    developper_faiblesses?: {
      introduction?: string;
      strategies?: Array<{
        faiblesse: string;
        strategie: string;
        exercice_quotidien: string;
        indicateur_progres: string;
      }>;
      conseil_mindset?: string;
    };
    conseils_carriere?: {
      orientations_ideales?: string[];
      erreurs_classiques?: string[];
      plan_action?: {
        court_terme: string;
        moyen_terme: string;
        long_terme: string;
      };
      message_motivation?: string;
    };
  };
}

/**
 * Generate Recommendations PDF (professional format, minimalist design)
 */
export const generateRecommendationsPdf = (options: RecommendationsPdfOptions): jsPDF | null => {
  const { profileName, recommendations } = options;
  
  if (!recommendations) return null;
  
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - 2 * margin;
  let y = margin;
  
  // Helper: Add section title with left bar accent (minimalist style matching profile PDF)
  const addSectionTitle = (title: string, color: [number, number, number] = COLORS.primary) => {
    if (y > pageHeight - 40) {
      pdf.addPage();
      y = margin;
    }
    y += 6;
    
    // Clean accent bar on left only (no background)
    pdf.setFillColor(...color);
    pdf.rect(margin, y - 3, 3, 10, 'F');
    
    // Section title - 14pt bold
    pdf.setFontSize(FONTS.titleSmall);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...color);
    pdf.text(title.toUpperCase(), margin + 8, y + 4);
    
    y += 14;
  };
  
  // Helper: Add text with word wrap (matching profile PDF style)
  const addText = (text: string, fontSize: number = 10, isBold: boolean = false, color: [number, number, number] = COLORS.text, indent: number = 0) => {
    pdf.setFontSize(fontSize);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    pdf.setTextColor(...color);
    const effectiveWidth = maxWidth - indent;
    const lines = pdf.splitTextToSize(text, effectiveWidth);
    
    const lineHeight = fontSize * 0.45;
    if (y + lines.length * lineHeight > pageHeight - 25) {
      pdf.addPage();
      y = margin;
    }
    
    lines.forEach((line: string) => {
      pdf.text(line, margin + indent, y);
      y += lineHeight;
    });
    y += 2;
  };
  
  // Helper: Add bullet point (matching profile PDF style)
  const addBullet = (text: string, color: [number, number, number] = COLORS.primary) => {
    pdf.setFontSize(FONTS.body);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.text);
    
    const lines = pdf.splitTextToSize(text, maxWidth - 12);
    
    if (y + lines.length * 5 > pageHeight - 25) {
      pdf.addPage();
      y = margin;
    }
    
    // Custom bullet point
    pdf.setFillColor(...color);
    pdf.circle(margin + 3, y - 1, 1.2, 'F');
    
    lines.forEach((wLine: string, idx: number) => {
      pdf.text(wLine, margin + 8, y);
      y += 5;
    });
    
    y += 1;
  };
  
  // Helper: Add action item with arrow
  const addActionItem = (label: string, text: string) => {
    if (y > pageHeight - 20) {
      pdf.addPage();
      y = margin;
    }
    
    pdf.setFontSize(FONTS.bodySmall);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.primary);
    pdf.text(label + ' : ', margin + 4, y);
    const labelWidth = pdf.getTextWidth(label + ' : ');
    
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.text);
    const lines = pdf.splitTextToSize(text, maxWidth - labelWidth - 8);
    pdf.text(lines[0] || '', margin + 4 + labelWidth, y);
    y += 4;
    
    // Remaining lines if any
    if (lines.length > 1) {
      for (let i = 1; i < lines.length; i++) {
        pdf.text(lines[i], margin + 8, y);
        y += 4;
      }
    }
    y += 2;
  };
  
  // =========================================================================
  // HEADER - Clean minimalist design (Green theme for recommendations)
  // =========================================================================
  
  const username = getUsername();
  
  // Green header bar
  pdf.setFillColor(...COLORS.recsGreen);
  pdf.rect(0, 0, pageWidth, 35, 'F');
  
  // Title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.white);
  pdf.text('Recommandations personnalisees basees sur votre profil', margin, 16);
  
  // Username
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text(username, margin, 28);
  
  y = 45;
  
  // Info box
  pdf.setFillColor(...COLORS.background);
  pdf.roundedRect(margin, y - 5, maxWidth, 14, 2, 2, 'F');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...COLORS.textMuted);
  pdf.text('Ce rapport contient des recommandations carriere, des strategies de developpement et un plan d\'action personnalise.', margin + 5, y + 3);
  y += 18;
  
  // =========================================================================
  // SECTION 1: EXPLOITER VOS FORCES
  // =========================================================================
  
  if (recommendations.exploiter_forces) {
    addSectionTitle('Exploiter vos forces', COLORS.recsGreen);
    
    if (recommendations.exploiter_forces.introduction) {
      addText(recommendations.exploiter_forces.introduction);
      y += 2;
    }
    
    recommendations.exploiter_forces.strategies?.forEach((strat, i) => {
      // Strategy title with bullet
      pdf.setFillColor(...COLORS.recsGreen);
      pdf.circle(margin + 3, y - 1, 1.2, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.text);
      pdf.setFontSize(FONTS.body);
      pdf.text(strat.titre, margin + 8, y);
      y += 6;
      
      // Description
      addText(strat.description, FONTS.body, false, COLORS.text, 4);
      
      // Action immediate
      addActionItem('Action immediate', strat.action_immediate);
      y += 3;
    });
    
    if (recommendations.exploiter_forces.pieges_a_eviter?.length) {
      y += 4;
      addText('Pieges a eviter:', FONTS.body, true);
      recommendations.exploiter_forces.pieges_a_eviter.forEach(piege => {
        addBullet(piege, COLORS.danger);
      });
    }
    
    y += 8;
  }
  
  // =========================================================================
  // SECTION 2: AXES DE DEVELOPPEMENT
  // =========================================================================
  
  if (recommendations.developper_faiblesses) {
    addSectionTitle('Axes de developpement', COLORS.recsGreen);
    
    if (recommendations.developper_faiblesses.introduction) {
      addText(recommendations.developper_faiblesses.introduction);
      y += 2;
    }
    
    recommendations.developper_faiblesses.strategies?.forEach((strat, i) => {
      // Faiblesse title with bullet
      pdf.setFillColor(...COLORS.recsGreen);
      pdf.circle(margin + 3, y - 1, 1.2, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.text);
      pdf.setFontSize(FONTS.body);
      pdf.text(strat.faiblesse, margin + 8, y);
      y += 6;
      
      // Strategy
      addText(strat.strategie, FONTS.body, false, COLORS.text, 4);
      
      // Exercice quotidien
      addActionItem('Exercice quotidien', strat.exercice_quotidien);
      
      // Indicateur de progres
      addActionItem('Indicateur de progres', strat.indicateur_progres);
      y += 3;
    });
    
    if (recommendations.developper_faiblesses.conseil_mindset) {
      y += 4;
      addText('Conseil mindset:', FONTS.body, true);
      addText(recommendations.developper_faiblesses.conseil_mindset, FONTS.body, false, COLORS.textMuted);
    }
    
    y += 8;
  }
  
  // =========================================================================
  // SECTION 3: STRATEGIE CARRIERE
  // =========================================================================
  
  if (recommendations.conseils_carriere) {
    addSectionTitle('Strategie Carriere', COLORS.recsGreen);
    
    if (recommendations.conseils_carriere.orientations_ideales?.length) {
      addText('Orientations ideales:', FONTS.body, true);
      recommendations.conseils_carriere.orientations_ideales.forEach((o, i) => {
        addBullet(`${o}`, COLORS.recsGreen);
      });
      y += 4;
    }
    
    if (recommendations.conseils_carriere.erreurs_classiques?.length) {
      addText('Erreurs a eviter:', FONTS.body, true);
      recommendations.conseils_carriere.erreurs_classiques.forEach(e => {
        addBullet(e, COLORS.danger);
      });
      y += 4;
    }
    
    if (recommendations.conseils_carriere.plan_action) {
      addText("Plan d'action:", FONTS.body, true);
      y += 2;
      
      // Court terme
      pdf.setFillColor(...COLORS.recsGreen);
      pdf.rect(margin, y - 3, 3, 10, 'F');
      pdf.setFontSize(FONTS.bodySmall);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.recsGreen);
      pdf.text('Court terme (30 jours)', margin + 6, y + 3);
      y += 8;
      addText(recommendations.conseils_carriere.plan_action.court_terme, FONTS.body, false, COLORS.text, 6);
      y += 4;
      
      // Moyen terme
      pdf.setFillColor(...COLORS.recsGreen);
      pdf.rect(margin, y - 3, 3, 10, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.recsGreen);
      pdf.text('Moyen terme (3-6 mois)', margin + 6, y + 3);
      y += 8;
      addText(recommendations.conseils_carriere.plan_action.moyen_terme, FONTS.body, false, COLORS.text, 6);
      y += 4;
      
      // Long terme
      pdf.setFillColor(...COLORS.recsGreen);
      pdf.rect(margin, y - 3, 3, 10, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...COLORS.recsGreen);
      pdf.text('Long terme (1-2 ans)', margin + 6, y + 3);
      y += 8;
      addText(recommendations.conseils_carriere.plan_action.long_terme, FONTS.body, false, COLORS.text, 6);
      y += 4;
    }
    
    // Motivation message
    if (recommendations.conseils_carriere.message_motivation) {
      y += 8;
      
      if (y > pageHeight - 40) {
        pdf.addPage();
        y = margin;
      }
      
      // Subtle divider
      pdf.setDrawColor(...COLORS.border);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 8;
      
      // Motivation text
      pdf.setFontSize(FONTS.body);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(...COLORS.recsGreen);
      const msgLines = pdf.splitTextToSize(recommendations.conseils_carriere.message_motivation, maxWidth);
      msgLines.forEach((line: string) => {
        pdf.text(line, pageWidth / 2, y, { align: 'center' });
        y += 5;
      });
    }
  }
  
  // =========================================================================
  // FOOTER
  // =========================================================================
  
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.3);
    pdf.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
    
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.textLight);
    pdf.setFont('helvetica', 'normal');
    pdf.text('SkillSprint Challenge - Recommandations', margin, pageHeight - 12);
    
    const dateText = new Date().toLocaleDateString('fr-FR');
    pdf.text(dateText, pageWidth / 2, pageHeight - 12, { align: 'center' });
    
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
  }
  
  return pdf;
};

/**
 * Generate Recommendations PDF blob URL
 */
export const generateRecommendationsPdfUrl = (options: RecommendationsPdfOptions): string | null => {
  const pdf = generateRecommendationsPdf(options);
  if (!pdf) return null;
  return generatePdfBlobUrl(pdf);
};

/**
 * Download Recommendations PDF
 */
export const downloadRecommendationsPdf = (options: RecommendationsPdfOptions): void => {
  const pdf = generateRecommendationsPdf(options);
  if (!pdf) return;
  const fileName = `Recommandations_${options.profileName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};
